import { Component, ViewChild, ElementRef, Inject, Optional, HostBinding, ChangeDetectionStrategy } from '@angular/core';
import { Partner } from '@app/shared/_models/common';
import { RouterLink } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { FormsModule } from '@angular/forms';

import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { PartnersService } from '@app/common/partners/partners.service';
import { CommonService } from '@app/common/common.service';
import { ValuesService } from '@app/common/values/values.service';
import { AlertService } from '@app/shared/alert.service';
import { C_value, Store } from '@app/shared/_models/common';
import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';

import * as constants from '@app/shared/constants';
import * as util from '@app/shared/util';

/** Une pastille de rôle, prête à rendre. */
export interface RolePill {
  id: number;
  short: string;   // 3-5 caractères, ce qui tient dans une piste de 96 px
  label: string;   // libellé du référentiel, en infobulle
  known: boolean;  // 1/2/3 sont colorés ; le reste est neutre
}

// ═══════════════════════════════════════════════════════════════════════════════════
// Ligne d'affichage : APLATIE une fois pour toutes à l'arrivée de la réponse.
//
// Le gabarit ne fait plus AUCUN appel de méthode par cellule. Avec 13 colonnes et les
// 201 lignes que le serveur peut renvoyer, la version « une méthode par cellule »
// coûtait ~2 600 appels à CHAQUE cycle de détection de changements (la page est en
// stratégie Eager). Ici, tout est calculé une fois par recherche.
//
// `raw` conserve l'objet EXACT reçu de l'API : c'est lui, et non la ligne aplatie, qui est
// rendu par dialogRef.close() — le contrat de sortie ne bouge pas d'un pouce.
//
// ⚠ Ce contrat n'a aujourd'hui AUCUN client : app-search-partner ouvre la fenêtre sans
// s'abonner à afterClosed() (search-partner.component.ts:504-513, la souscription y est en
// commentaire), si bien que le tiers choisi est perdu à la fermeture. Le défaut est chez
// l'appelant, pas ici ; il reste à corriger. (search-partner-old, qui a le même trou, n'est
// instancié nulle part : son sélecteur app-search-partner-old n'apparaît dans aucun gabarit.)
// ═══════════════════════════════════════════════════════════════════════════════════
export interface PartnerRow {
  raw: any;
  id: number;
  store: number | null;
  roles: RolePill[];
  activity: string;
  civility: string;
  name: string;
  firstname: string;
  address: string;
  zip: string;
  city: string;
  tel1: string;
  email: string;
  addrCount: number;
  addrHint: string;
}

@Component({
  selector: 'app-partners',
  standalone: true,
  templateUrl: './partners.component.html',
  styleUrls: ['./partners.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MenuCommonComponent, MatDialogModule, MatButtonModule, MatIconModule, MatTooltipModule, MatProgressSpinnerModule, RouterLink, FormsModule]
})
export class PartnersComponent {

  // ───────────────────────────────────────────────────────────── critères de recherche
  public searchname: string = '';    // Name Search field
  public searchtown: string = '';    // Town Search field (jamais alimenté : le champ est masqué depuis longtemps)
  public searchact: string = '0';    // Activity search field. Defaults to 0 == ALL
  public searchstore: string = '0';  // Store Defaults to 0 == ALL

  /** Rôles retenus. Le défaut de la page vient du paramétrage (constants.PARTNERS_DEFAULT_ROLES,
   *  aujourd'hui « Client ») ; en fenêtre, l'appelant impose les siens via dialogData.roles.
   *  Le tableau de la constante est COPIÉ : onRoleToggle empile dedans, et muter la constante
   *  changerait le défaut pour toute l'application. */
  public roles: number[] = [...constants.PARTNERS_DEFAULT_ROLES];

  /** Rôles à l'ouverture, pour que « Réinitialiser les filtres » y revienne — au défaut de la
   *  page, ou à ceux que l'appelant a imposés en fenêtre. */
  private initialRoles: number[] = [...constants.PARTNERS_DEFAULT_ROLES];

  // ───────────────────────────────────────────────────────────── référentiels
  public activities: C_value[] = [];
  public map_activities = new Map<any, string>();
  /** Libellés du référentiel des rôles (c_values 2 : 12 valeurs, de « Interne » à
   *  « Banque »). La liste dépasse les trois rôles filtrables : un tiers peut
   *  porter « payeur AMO » ou « Prescripteur », et sa pastille doit le dire. */
  public map_roles = new Map<number, string>();
  /** « Tous » en tête, puis le référentiel des établissements (remplace les trois
   *  <option> écrites en dur dans le gabarit, dont deux MENTAIENT : `value="2"` était
   *  libellée « Pdv La Valette » alors que c_store.id 2 est « Pdv La Seine », et le
   *  magasin 3 — le vrai « Pdv La Valette » — était injoignable). */
  public stores: Store[] = [{ id: 0, name: 'Tous' }];
  /** Légende id → nom des établissements, affichée en infobulle sur l'entête « Site ». */
  public storeLegend = '';

  // ───────────────────────────────────────────────────────────── résultats
  private items: any[] = [];             // réponse brute, conservée pour re-aplatir si un référentiel arrive après
  public rows: PartnerRow[] = [];
  public is_loading = false;
  public hasSearched = false;
  public roleCounts = { [constants.PARTNER_ROLE_CUSTOMER]: 0, [constants.PARTNER_ROLE_SUPPLIER]: 0, [constants.PARTNER_ROLE_MANUFACTURER]: 0 } as Record<number, number>;

  /** Le serveur s'arrête APRÈS avoir empilé la 201e ligne (mercalink-server,
   *  common/src/partner/model.rs : `ret.push(row); nb += 1; if nb > 200 { break }`).
   *  La borne est donc 201, pas 200 : un résultat complet de 200 tiers n'est PAS tronqué. */
  public readonly SERVER_LIMIT = 201;
  public get isTruncated(): boolean { return this.rows.length >= this.SERVER_LIMIT; }

  // ───────────────────────────────────────────────────────────── colonnes
  // [clé, libellé, classe d'alignement, sens du tri courant]
  // Ordre postal des colonnes d'adresse conservé : Appart. (street3), Bât. (street2), Rue (street1)
  //
  // « Tél. 2 » a été RETIRÉ : c_partner.tel2 est vide sur les 79 006 tiers de la base de
  // développement, et ne figure ni dans l'INSERT ni dans l'UPDATE du serveur
  // (common/src/partner/model.rs) — la colonne ne pouvait jamais rien afficher.
  //
  // « Appart. » / « Bât. » / « Rue » ont fusionné en UNE colonne « Adresse ». Les trois
  // libellés étaient FAUX : en base, street2 porte le plus souvent la voie (« imp les
  // pivoines », « ROUTE DE JANAS », « ZA des playes ») et street1 le complément (« villa la
  // sebisa », « LA BEAUCAIRE BAT 32 »), et street3 tantôt l'un tantôt l'autre. Les trois
  // champs ne sont pas saisis selon une règle : seule leur CONCATÉNATION dans l'ordre
  // postal (street3, street2, street1) — l'ordre que retenait déjà l'ancien gabarit —
  // constitue une adresse lisible. Trois pistes dont deux vides à 98 % et 69 % laissent
  // d'ailleurs la place à une colonne pleine.
  public displayedColumnsInfo: [string, string, string, boolean?][] = [
    ['id', 'Id', 'num'],
    ['store', 'Site', 'center'],
    ['roles', 'Rôles', ''],
    ['activity', 'Activité', ''],
    ['name', 'Nom', ''],
    ['firstname', 'Prénom', ''],
    ['address', 'Adresse', ''],
    ['zip', 'CP', 'num'],
    ['city', 'Ville', ''],
    ['tel1', 'Téléphone', ''],
    ['email', 'E-mail', ''],
  ];

  /** Les trois rôles filtrables, dans l'ordre où ils sont proposés et affichés. */
  public readonly ROLE_ORDER: number[] = [constants.PARTNER_ROLE_CUSTOMER, constants.PARTNER_ROLE_SUPPLIER, constants.PARTNER_ROLE_MANUFACTURER];

  /** Repli si le référentiel n'est pas encore arrivé (ou plus joignable) : les bascules
   *  de la barre de filtres doivent porter un libellé dès le premier rendu. */
  private readonly ROLE_LABELS: Record<number, string> = {
    [constants.PARTNER_ROLE_INTERNAL]: 'Interne', [constants.PARTNER_ROLE_MANUFACTURER]: 'Fabricant',
    [constants.PARTNER_ROLE_SUPPLIER]: 'Fournisseur', [constants.PARTNER_ROLE_CUSTOMER]: 'Client',
    [constants.PARTNER_ROLE_ADDRESS]: 'Adresse', [constants.PARTNER_ROLE_CONTACT]: 'Contact',
    [constants.PARTNER_ROLE_PAYER_AMO]: 'payeur AMO', [constants.PARTNER_ROLE_PAYER_AMC]: 'payeur AMC',
    [constants.PARTNER_ROLE_INTRODUCER]: 'Apporteur', [constants.PARTNER_ROLE_REPRESENTATIVE]: 'Représentant',
    [constants.PARTNER_ROLE_PRESCRIBER]: 'Prescripteur', [constants.PARTNER_ROLE_BANK]: 'Banque',
  };
  private readonly ROLE_SHORT: Record<number, string> = {
    [constants.PARTNER_ROLE_INTERNAL]: 'Int', [constants.PARTNER_ROLE_MANUFACTURER]: 'Fab',
    [constants.PARTNER_ROLE_SUPPLIER]: 'Frs', [constants.PARTNER_ROLE_CUSTOMER]: 'Cli',
    [constants.PARTNER_ROLE_ADDRESS]: 'Adr', [constants.PARTNER_ROLE_CONTACT]: 'Ctc',
    [constants.PARTNER_ROLE_PAYER_AMO]: 'AMO', [constants.PARTNER_ROLE_PAYER_AMC]: 'AMC',
    [constants.PARTNER_ROLE_INTRODUCER]: 'App', [constants.PARTNER_ROLE_REPRESENTATIVE]: 'Rep',
    [constants.PARTNER_ROLE_PRESCRIBER]: 'Presc', [constants.PARTNER_ROLE_BANK]: 'Bq',
  };

  /** cli_titre repris tel quel par la migration orthop (_batch/migorthop/src/partner/mod.rs) :
   *  la fiche tiers n'en édite que trois, mais la base en porte huit. */
  private readonly CIVILITIES: Record<number, string> = {
    1: 'M.', 2: 'Mme', 3: 'Mlle', 4: 'Ets', 5: 'Ste', 6: 'Dr', 7: 'S.C.M', 8: 'S.D.F',
  };

  @ViewChild('searchNameInput') private searchNameInput?: ElementRef<HTMLInputElement>;

  /** UNE seule source de vérité pour « suis-je en fenêtre ? », et c'est `dialogRef` : c'est
   *  lui qui décide du comportement au clic, et MatDialog l'injecte toujours, que l'appelant
   *  ait passé un `data` ou non. `dialogData` ne sert plus qu'à LIRE searchcriteria et roles.
   *  Le gabarit interrogeait `dialogData` et le code `dialogRef` : deux réponses possibles
   *  pour une même question. */
  public get isDialog(): boolean { return !!this.dialogRef; }

  /** La page pose son propre fond et sa hauteur minimale ; en fenêtre, c'est le conteneur
   *  mat-dialog qui fixe la boîte, et la mise en page bascule en colonne (voir le SCSS). */
  @HostBinding('class.in-dialog') get hostInDialog(): boolean { return this.isDialog; }

  constructor(
    private partnersService: PartnersService,
    private commonService: CommonService,
    private valuesService: ValuesService,
    private alert: AlertService,
    private dialog: MatDialog,
    @Optional() public dialogRef: MatDialogRef<PartnersComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any,
  ) { }

  /** getValues$ vit sur un BehaviorSubject : il ne complète JAMAIS. Sans ce tableau et le
   *  ngOnDestroy qui va avec, chacune des ouvertures de la fenêtre — neuf gabarits
   *  l'instancient via app-search-partner — laissait un abonné de plus sur le référentiel. */
  private subs: Subscription[] = [];

  ngOnInit(): void {
    this.subs.push(this.valuesService.getValues$(constants.C_VALUES_CUSTOMER_ACT)  // Get activities
      .subscribe({ next: (data: any) => {
        this.map_activities.clear();
        for (const a of data ?? []) this.map_activities.set(a.id, a.name);
        // « Toutes » est ajouté dans un TABLEAU NEUF. L'ancienne écriture faisait
        // `this.activities = data` puis `unshift(...)` : elle mutait le tableau que
        // ValuesService rejoue à tous ses abonnés (shareReplay), si bien que l'option
        // « Toutes » finissait par apparaître dans la liste d'activités de la FICHE tiers,
        // et s'y dupliquait à chaque nouvelle émission du référentiel.
        const toutes = new C_value();
        toutes.id = 0; toutes.name = 'Toutes'; toutes.seq = 0;
        this.activities = [toutes, ...(data ?? [])];
        this.refreshReferentialCells();   // le référentiel peut arriver après la réponse
      }, error: (e) => console.log('ERREUR chargement des activités : ', e?.error ?? e) }));

    this.subs.push(this.valuesService.getValues$(constants.C_VALUES_PARTNER_ROLE)  // Get partner roles
      .subscribe({ next: (data: any) => {
        this.map_roles.clear();
        for (const r of data ?? []) this.map_roles.set(Number(r.id), r.name);
        this.refreshReferentialCells();
      }, error: (e) => console.log('ERREUR chargement des rôles : ', e?.error ?? e) }));

    this.subs.push(this.commonService.getStores().subscribe({
      next: (stores: any) => {
        // « Tous » (id 0) reste en tête. Les id <= 0 de l'API sont écartés : 0 est le
        // SENTINELLE de « Tous » (la garde de recherche est `searchstore != '0'`), et deux
        // <option> de même valeur casseraient aussi la clé de suivi du @for du gabarit.
        this.stores = [...this.stores, ...(stores ?? []).filter((s: any) => (s?.id ?? 0) > 0)];
        this.storeLegend = this.buildStoreLegend();
      },
      error: (e) => console.log('ERREUR chargement des établissements : ', e?.error ?? e)
    }));

    if (this.dialogData && this.dialogData.searchcriteria) this.searchname = this.dialogData.searchcriteria;  // Name is provided by caller
    if (this.dialogData && this.dialogData.roles && this.dialogData.roles.length) {
      this.roles = [...this.dialogData.roles];   // Roles are provided by caller
    }
    this.initialRoles = [...this.roles];
  }

  ngAfterViewInit(): void {
    // Le dialogue s'ouvre avec le texte déjà saisi dans app-search-partner : on lance la
    // recherche au lieu de la faire redemander. Sans critère, onSearch() ne fait rien —
    // la page autonome ne déclenche donc aucune requête à l'ouverture.
    setTimeout(() => {
      this.searchNameInput?.nativeElement?.focus();
      if (this.searchname.length > 0) this.onSearch();
    });
  }

  onKeydown(event: any) {      // TEMPLATE event.key === "Enter"
    if (event.key === 'Enter') {
      this.onSearch();
    }
  }

  /** Numéro de la recherche en cours. La refonte relance la recherche à chaque changement de
   *  select et à chaque bascule de rôle : deux requêtes peuvent désormais se croiser, et rien
   *  ne garantit que le serveur réponde dans l'ordre où on l'interroge. Sans ce numéro, une
   *  réponse tardive écrase une réponse plus récente et la liste ne correspond plus aux
   *  filtres affichés. */
  private searchSeq = 0;

  onSearch(): void {
    let obs: Observable<any>;
    if (this.searchname.length > 0 || this.searchact != '0' || this.searchstore != '0') {  // Must input a criteria
      this.is_loading = true;
      const seq = ++this.searchSeq;
      // `+this.searchname > 0` et non `!isNaN(...)` : `+''` vaut 0, si bien qu'un champ nom
      // VIDE partait sur la branche NIR — celle qui n'envoie PAS le paramètre `roles`.
      // Choisir une activité ou un site sans taper de nom rendait alors des tiers de
      // n'importe quel rôle, alors que les pastilles restaient allumées à l'écran et qu'en
      // fenêtre c'est l'appelant qui impose le rôle attendu. La borne à zéro écarte du même
      // coup « 0 », « 00 », « 0.0 », que le service laissait tomber (`if (nir)`) : ces
      // saisies-là sont désormais cherchées comme du texte.
      if (+this.searchname > 0) {  // We have a NIR
        // La recherche par NIR n'envoie VOLONTAIREMENT pas `roles` : un NIR désigne un patient
        // précis, et le croiser avec le défaut [1] (Fabricant) ne rendrait jamais rien.
        obs = this.partnersService.getPartnersByFilter(undefined, undefined, (this.searchact == '0' ? '' : this.searchact), (this.searchstore == '0' ? '' : this.searchstore), +this.searchname);
      } else {
        obs = this.partnersService.getPartnersByFilter(util.postgressearchstring(this.searchname), this.searchtown, (this.searchact == '0' ? '' : this.searchact), (this.searchstore == '0' ? '' : this.searchstore), undefined, this.roles);
      }
      obs.subscribe({
        next: (items: any) => {
          if (seq !== this.searchSeq) return;   // réponse d'une recherche périmée
          this.items = items ?? [];
          this.rows = this.buildRows(this.items);
          this.clearSortArrows();
          this.is_loading = false;
          this.hasSearched = true;
        },
        error: (e) => {
          console.log('ERREUR Serveur : ', e.error);
          if (seq !== this.searchSeq) return;
          this.items = [];
          this.rows = this.buildRows([]);
          this.is_loading = false;
          this.hasSearched = true;
          this.alert.error('ERREUR Serveur : ' + e.error);  // TEMPLATE subscribe call
        },
        complete: () => {
          // getPartnersByFilter rend EMPTY — un flux qui COMPLÈTE SANS RIEN ÉMETTRE — dès que
          // la requête ne porte aucun paramètre utile. Le cas se produit vraiment : « 0 » seul
          // dans le champ nom passe la garde ci-dessus (length > 0), part sur la branche NIR,
          // et `if (nir)` est faux pour 0. Sans ce complete, `is_loading` resterait à true et
          // le bouton de recherche tournerait indéfiniment.
          if (this.is_loading && seq === this.searchSeq) {
            this.items = [];
            this.rows = this.buildRows([]);
            this.clearSortArrows();
            this.is_loading = false;
            this.hasSearched = true;
          }
        }
      });
    } else {
      // Plus aucun critère. La refonte relance la recherche à chaque changement de filtre :
      // sans cette branche, ramener le dernier filtre à sa valeur neutre laissait à l'écran
      // la liste du filtre PRÉCÉDENT, sous une barre vide. On revient à l'invite.
      this.searchSeq++;                       // une réponse en vol ne repeuplera plus la liste
      this.items = [];
      this.rows = this.buildRows([]);
      this.clearSortArrows();
      this.is_loading = false;
      this.hasSearched = false;
    }
  }

  // La signature prend la LIGNE D'AFFICHAGE et ferme avec `row.raw` : c'est le compilateur,
  // et non une relecture, qui garantit que l'appelant reçoit l'objet API tel quel.
  onClickRow(row: PartnerRow, e: MouseEvent): void {
    // Mode page : c'est le routerLink de la ligne qui navigue — il laisse passer les
    // ctrl/cmd-clics au navigateur, on n'y touche pas.
    // Mode fenêtre : la ligne n'a pas de routerLink, elle rend le tiers à l'appelant.
    if (this.isDialog && util.isPlainLeftClick(e)) this.dialogRef.close(row.raw);
  }

  /** Fiche vierge (id 0) PAR-DESSUS la fenêtre, au triplet des trois autres ouvertures ; elle ne rend
   *  un objet que si le tiers a été enregistré — on le rend alors comme un clic sur sa ligne. */
  openNewPartner(): void {
    // Import dynamique : la fiche importe app-search-partner, qui importe cette liste (cycle).
    import('@app/common/partner/partner.component').then(({ PartnerComponent }) => {
      const ref = this.dialog.open(PartnerComponent, {
        width: '96%',
        height: '94%',
        maxWidth: '96vw',
        data: { mode: 'DIALOG', id: 0 },
      });
      this.subs.push(ref.afterClosed().subscribe((p: any) => { if (p?.id) this.dialogRef?.close(p); }));
    });
  }

  /** Mode fenêtre au clavier : sans routerLink la ligne n'a pas d'attribut href, donc pas
   *  de comportement natif sur Entrée. Le gabarit lui donne un tabindex, cette méthode
   *  l'active. En mode page, l'ancre porte un href : le navigateur s'en charge. */
  onRowEnter(row: PartnerRow): void {
    if (this.isDialog) this.dialogRef.close(row.raw);
  }

  onCancel(): void {
    this.searchname = '';
    this.searchtown = '';
    this.searchact = '0';
    this.searchstore = '0';
    // « Réinitialiser » remet AUSSI les rôles dans l'état d'ouverture : le défaut de la page,
    // ou ceux que l'appelant a imposés en fenêtre — jamais un défaut qui contredirait l'écran
    // qui a ouvert la fenêtre.
    this.roles = [...this.initialRoles];
    // Passe par onSearch(), dont la branche « aucun critère » vide la liste, remet l'invite
    // et incrémente searchSeq — sans quoi une réponse encore en vol repeuplait la liste
    // juste après la réinitialisation.
    this.onSearch();
    this.searchNameInput?.nativeElement?.focus();
  }

  clearName(): void {
    this.searchname = '';
    this.onSearch();   // les autres filtres peuvent rester : la liste suit ce que montre la barre
    this.searchNameInput?.nativeElement?.focus();
  }

  onRoleToggle(roleId: number, checked: boolean) {
    if (checked) {
      if (!this.roles.includes(roleId)) { this.roles.push(roleId); }
    } else {
      this.roles = this.roles.filter(r => r !== roleId);
    }
    this.onSearch();
  }

  // ───────────────────────────────────────────────────────────── mise en forme des lignes
  //
  // Source d'adresse : l'adresse de facturation par défaut du tiers (sa ligne c_partner_partner
  // de rôle -1 avec is_default), sinon celle de livraison, sinon la première de ses adresses
  // (lignes de rôle -5), à défaut la ligne c_partner elle-même — Partner.mainAddress.
  //
  // Téléphone et e-mail sont cherchés sur la LIGNE PRINCIPALE PUIS sur l'adresse : les deux
  // sont légitimes. La reprise orthop écrit le téléphone sur la ligne principale (33 445 tiers
  // sur 79 006 en base de dev) tandis que l'e-mail se retrouve tantôt sur l'une, tantôt sur
  // l'autre. Sans ce repli, la colonne paraissait vide là où la donnée existe.
  private buildRows(items: any[]): PartnerRow[] {
    const counts: Record<number, number> = Object.fromEntries(this.ROLE_ORDER.map(r => [r, 0]));
    const rows = (items ?? []).map((p: any) => {
      const a: any = Partner.mainAddress(p) ?? p;
      const roles = this.buildRolePills(p?.roles);
      for (const pill of roles) if (counts[pill.id] !== undefined) counts[pill.id]++;
      const n = Partner.addresses(p).length;
      return {
        raw: p,
        id: p?.id,
        store: p?.store ?? null,
        roles,
        // `typ` pointe le référentiel « Activité CLIENT » (c_values 5) : il n'a de sens que
        // pour un tiers effectivement client. Les 296 fabricants et fournisseurs de la base
        // portent TOUS typ = 2, ce qui affichait « Infirmier » pour chacun d'eux. L'ancien
        // gabarit masquait le défaut en mettant le RÔLE dans cette colonne — ce que la
        // colonne « Rôles » fait désormais, sans mentir sur l'activité.
        activity: this.activityOf(p, roles),
        civility: this.CIVILITIES[p?.civility] ?? '',
        name: p?.name ?? '',
        firstname: p?.firstname ?? '',
        address: [a?.street3, a?.street2, a?.street1].map(v => (v ?? '').trim()).filter(v => v).join(', '),
        zip: a?.zip ?? '',
        city: a?.city ?? '',
        tel1: p?.tel1 || a?.tel1 || '',
        email: p?.email || a?.email || '',
        addrCount: n,
        addrHint: n > 1 ? `${n} adresses enregistrées ; la première (facturation) est affichée.` : '',
      } as PartnerRow;
    });
    this.roleCounts = counts;
    return rows;
  }

  /** L'activité d'un tiers — vide pour qui n'est pas client (voir buildRows). UNE règle pour
   *  les deux moments où la cellule se calcule : à la construction et quand le référentiel
   *  arrive après la réponse (refreshReferentialCells recopiait l'activité sur tous les
   *  rôles, et « Infirmier » revenait sur les fabricants au second passage). */
  private activityOf(p: any, roles: RolePill[]): string {
    return roles.some(x => x.id === constants.PARTNER_ROLE_CUSTOMER) ? (this.map_activities.get(p?.typ) ?? '') : '';
  }

  /** Toutes les valeurs de c_partner.roles deviennent une pastille : les trois filtrables
   *  d'abord et colorées, les autres ensuite et neutres. Filtrer sur [3,2,1] laissait la
   *  cellule VIDE pour un « payeur AMO » ou un « Prescripteur ». */
  private buildRolePills(roles: any): RolePill[] {
    const ids: number[] = (roles ?? []).map((r: any) => Number(r)).filter((r: number) => !isNaN(r));
    const known = this.ROLE_ORDER.filter(r => ids.includes(r));
    const others = ids.filter(r => !this.ROLE_ORDER.includes(r)).sort((x, y) => Math.abs(x) - Math.abs(y));
    return [...known, ...others].map(id => ({
      id,
      short: this.ROLE_SHORT[id] ?? String(id),
      label: this.roleLabel(id),
      known: this.ROLE_ORDER.includes(id),
    }));
  }

  /** Un référentiel peut arriver APRÈS la réponse. On remet alors à jour les SEULES cellules
   *  qui en dépendent, en place : reconstruire la liste la remettrait dans l'ordre du serveur
   *  alors que la flèche de tri, elle, resterait affichée sur sa colonne. */
  private refreshReferentialCells(): void {
    for (const r of this.rows) {
      r.roles = this.buildRolePills(r.raw?.roles);
      r.activity = this.activityOf(r.raw, r.roles);
    }
  }

  // ───────────────────────────────────────────────────────────── tri
  // Même mécanique que le sélecteur de commandes : le clic d'entête bascule le sens, et
  // displayedColumnsInfo porte la flèche de la SEULE colonne triée.
  //
  // ⚠ Le tri porte sur les lignes AFFICHÉES. La requête serveur n'a ni ORDER BY ni LIMIT :
  // elle s'arrête à la 201e ligne rendue par PostgreSQL, dans un ordre non déterministe.
  // Trier ne rapproche donc pas les tiers manquants — c'est le rôle de la puce « tronquée ».
  sortRows(order: boolean, name: string) {
    this.rows = [...this.rows].sort((a: any, b: any) => {
      const valA = this.getSortValue(a, name);
      const valB = this.getSortValue(b, name);
      const isNumeric = (v: any) => v !== '' && v !== null && !isNaN(parseFloat(v)) && isFinite(v);
      if (isNumeric(valA) && isNumeric(valB)) {
        return order ? parseFloat(valA) - parseFloat(valB) : parseFloat(valB) - parseFloat(valA);
      }
      return order ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
    });
    this.displayedColumnsInfo = this.displayedColumnsInfo.map(col =>
      col[0] === name ? [col[0], col[1], col[2], order] : [col[0], col[1], col[2]]
    );
  }

  /** Une NOUVELLE liste arrive dans l'ordre du serveur — qui n'a aucun ORDER BY. Laisser la
   *  flèche sur sa colonne ferait annoncer à l'entête un tri que la liste n'a plus, et le
   *  clic suivant repartirait en DESCENDANT sur une liste visiblement non triée. */
  private clearSortArrows(): void {
    this.displayedColumnsInfo = this.displayedColumnsInfo.map(col => [col[0], col[1], col[2]]);
  }

  getSortValue(r: PartnerRow, name: string): any {
    switch (name) {
      case 'id': return r.id ?? 0;
      case 'store': return r.store ?? -1;
      // Rang de la première pastille (Client, puis Fournisseur, puis Fabricant, puis le
      // reste), pas la chaîne : sinon le tri suivrait l'ordre alphabétique des abréviations.
      case 'roles': return r.roles.length ? this.rolesRank(r.roles[0].id) : 99;
      case 'name': return (r.name ?? '') + ' ' + (r.firstname ?? '');
      // L'adresse commence par le complément quand il existe : on trie sur la voie, qui est
      // ce que l'œil cherche. À défaut de voie, la chaîne entière.
      case 'address': return r.address;
      default: return (r as any)[name] ?? '';
    }
  }

  private rolesRank(id: number): number {
    const i = this.ROLE_ORDER.indexOf(id);
    return i === -1 ? this.ROLE_ORDER.length + Math.abs(id) : i;   // |id| : les rôles système sont négatifs
  }

  /** Infobulles d'entête : '' = pas d'infobulle affichée. */
  colSortHint(colKey: string): string {
    if (colKey === 'store') return this.storeLegend;
    if (colKey === 'roles') return this.ROLE_ORDER.map(r => `${this.ROLE_SHORT[r]} — ${this.roleLabel(r)}`).join('\n')
      + '\nLes autres rôles du référentiel sont rendus en gris.';
    return '';
  }

  // La colonne « Site » ne tient qu'un NUMÉRO d'établissement : la correspondance id → nom
  // n'est écrite nulle part sur la page. On la donne en infobulle d'entête, construite une
  // fois à l'arrivée du référentiel (le gabarit interroge colSortHint() à chaque cycle de
  // détection : rien ne doit y être recalculé).
  private buildStoreLegend(): string {
    const lignes = this.stores
      .filter(store => (store.id ?? 0) > 0)
      .map(store => `${store.id} — ${store.name ?? ''}`.trim());
    return lignes.length ? 'Établissements :\n' + lignes.join('\n') : '';
  }

  roleLabel(role: number): string { return this.map_roles.get(role) ?? this.ROLE_LABELS[role] ?? ('Rôle ' + role); }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
  }
}
